import { z } from 'zod';

/**
 * AI Reports — AI-written analytical reports about the business.
 *
 * The server gathers a deterministic, permission-gated, currency-labelled data
 * snapshot for the requested period and an LLM writes Markdown prose over it
 * (the model never queries the DB). The snapshot is stored on the row so every
 * number in a report can be audited. Gated by the `ai_reports` module +
 * `ai_reports.read` / `ai_reports.manage`.
 */

export const AI_REPORT_TYPES = [
  'business_summary',
  'sales_performance',
  'inventory_health',
  'finance_overview',
  'custom_question',
] as const;
export type AiReportType = (typeof AI_REPORT_TYPES)[number];

export type AiReportStatus = 'running' | 'succeeded' | 'failed';

/** Report prose language (independent of the staff UI language). */
export const AiReportLanguageSchema = z.enum(['ar', 'en']);
export type AiReportLanguage = z.infer<typeof AiReportLanguageSchema>;

/** A real calendar date in YYYY-MM-DD (rejects 2026-13-45 etc.). */
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00.000Z`)), 'invalid calendar date');

/**
 * POST /ai-reports. Period presets are a client-side convenience — the server
 * only ever sees resolved dates (inclusive from/to).
 */
export const AiReportCreateSchema = z
  .object({
    type: z.enum(AI_REPORT_TYPES),
    periodFrom: CalendarDate,
    periodTo: CalendarDate,
    language: AiReportLanguageSchema,
    /** Optional custom title; the server derives one from type + period when absent. */
    title: z.string().trim().max(200).optional(),
    /** custom_question: what to ask the analyst about the period. */
    question: z.string().trim().max(2000).optional(),
  })
  .refine((v) => v.periodFrom <= v.periodTo, { message: 'periodFrom must not be after periodTo', path: ['periodFrom'] })
  // A year is the widest report that still fits a model context comfortably
  // (long spans also bucket the sales series monthly server-side).
  .refine(
    (v) => Date.parse(`${v.periodTo}T00:00:00Z`) - Date.parse(`${v.periodFrom}T00:00:00Z`) <= 366 * 86_400_000,
    { message: 'period must not exceed one year', path: ['periodTo'] },
  )
  .refine((v) => v.type !== 'custom_question' || !!v.question?.trim(), {
    message: 'question is required for custom_question reports',
    path: ['question'],
  });
export type AiReportCreate = z.infer<typeof AiReportCreateSchema>;

/** List row (GET /ai-reports) — no content/snapshot, they can be large. */
export interface AiReportSummary {
  id: string;
  type: AiReportType;
  title: string;
  language: AiReportLanguage;
  periodFrom: string;
  periodTo: string;
  status: AiReportStatus;
  /** Machine code when failed: ai_not_configured | ai_timeout | ai_provider_error | stale | … */
  errorCode: string | null;
  provider: string | null;
  model: string | null;
  createdByName: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Full row (GET /ai-reports/:id) — the poll target and viewer payload. */
export interface AiReport extends AiReportSummary {
  question: string | null;
  /** The generated Markdown, null until status is `succeeded`. */
  contentMd: string | null;
  /** The exact data pack the LLM wrote over (audit trail; shape varies by type). */
  snapshot: AiReportSnapshot;
  tokensIn: number | null;
  tokensOut: number | null;
}

/**
 * The deterministic data pack. Sections are nullable — a section the creating
 * user's permissions did not allow is `{ denied: true }` so the prose can say
 * "not included" instead of inventing numbers. All amounts are decimal STRINGS
 * tagged with a currency code; nothing is ever summed across currencies.
 */
export interface AiReportSnapshot {
  baseCurrencyCode: string;
  /** currency code -> display decimals (IQD 0, USD 2, …). */
  currencyExponents: Record<string, number>;
  periodFrom: string;
  periodTo: string;
  sections: Record<string, unknown>;
}

// ---- figures ---------------------------------------------------------------
// The report page renders real charts/tables from the SNAPSHOT (never from the
// prose). The model only PLACES them: it may drop a `[[chart:<id>]]` marker on
// its own line and the viewer swaps it for the matching figure. Which figures
// exist is a pure function of the snapshot, so the generation prompt (server)
// and the renderer (web) agree by construction.

export const AI_REPORT_FIGURE_IDS = [
  'sales_trend',
  'sales_totals',
  'top_sellers',
  'top_customers',
  'receivables_aging',
  'payables_aging',
  'pnl_breakdown',
  'profit_by_channel',
  'low_stock',
  'balance_sheet',
] as const;
export type AiReportFigureId = (typeof AI_REPORT_FIGURE_IDS)[number];

/**
 * Matches a line that IS a figure marker; the capture is the id (validated
 * against the list by callers). Deliberately tolerant of the wrappings models
 * actually produce — a leading bullet, **bold**, or trailing punctuation —
 * because a near-miss that fell through to prose would print the raw protocol
 * token to the reader.
 */
export const AI_REPORT_FIGURE_TOKEN = /^\s*(?:[-•*]\s*)?(?:\*\*)?\[\[chart:([a-z_]+)\]\](?:\*\*)?[\s.،:]*$/;

/** Any marker occurrence, wherever it sits — for scrubbing prose and clipboard text. */
const ANY_FIGURE_MARKER = /\[\[chart:[a-z_]+\]\]/gi;

/** Clean the markdown for human consumption (copy/share): marker lines out, inline markers scrubbed. */
export function stripAiReportFigureMarkers(md: string): string {
  return md
    .split('\n')
    .filter((line) => !AI_REPORT_FIGURE_TOKEN.test(line))
    .map((line) => line.replace(ANY_FIGURE_MARKER, '').trimEnd())
    .join('\n');
}

/** A permission-denied section draws nothing; otherwise hand back the object/array. */
function usable(section: unknown): Record<string, unknown> | null {
  if (!section || typeof section !== 'object') return null;
  if ((section as { denied?: boolean }).denied) return null;
  return section as Record<string, unknown>;
}

function len(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

/** The figures THIS snapshot can actually draw (section present, allowed, non-empty). */
export function availableAiReportFigures(snapshot: AiReportSnapshot): AiReportFigureId[] {
  const s = snapshot.sections ?? {};
  const sales = usable(s.sales);
  const pnl = usable(s.profitAndLoss);
  const bs = usable(s.balanceSheet);
  const out: AiReportFigureId[] = [];
  if (len(sales?.seriesBaseCurrency) >= 2) out.push('sales_trend');
  if (len(sales?.totalsByCurrency) > 0) out.push('sales_totals');
  if (len(usable(s.topSellers)) > 0) out.push('top_sellers');
  if (len(usable(s.topCustomers)) > 0) out.push('top_customers');
  if (len(usable(s.receivablesAging)?.totalsByCurrency) > 0) out.push('receivables_aging');
  if (len(usable(s.payablesAging)?.totalsByCurrency) > 0) out.push('payables_aging');
  if (len(pnl?.topRevenueAccounts) > 0 || len(pnl?.topExpenseAccounts) > 0) out.push('pnl_breakdown');
  if (len(usable(s.profit)?.byChannel) > 0) out.push('profit_by_channel');
  if (len(usable(s.lowStock)) > 0) out.push('low_stock');
  if (len(bs?.topAssets) > 0 || len(bs?.topLiabilities) > 0) out.push('balance_sheet');
  return out;
}
